/* eslint-disable default-param-last */
import { Vector3 } from 'three';
import { EElementType } from '../../@types/Enums';
import circleSVG from '../../assets/elements/element_circle.svg';
import rectangleSVG from '../../assets/elements/element_rectangle.svg';
import { DEFAULT_EMPTY_TEXTURE } from '../../utils/constants';
import { serializeTexture } from '../../utils/export/serializeTexture';
import Entity, { IEntity } from '../abstract/Entity';
import { IAttribute } from './AttributeModel';
import { IObject } from './ObjectModel';

export interface IElement extends IEntity {
  type: EElementType;
  pathToSvg: string;

  length: number;
  width: number;

  sideC?: number;
  triangleAngle?: number;

  position: Vector3;
  rotation: number;
  texture?: any | null;
  description?: string;
  useAsTextField?: boolean;
  fontSize?: number;
  object?: IObject;
}

class ElementModel extends Entity implements IElement {
  type: EElementType;

  pathToSvg: string;

  length: number;

  width: number;

  sideC?: number;

  triangleAngle?: number;

  position: Vector3;

  rotation: number;

  texture?: any | null;

  description?: string;

  useAsTextField?: boolean;

  fontSize?: number;

  object?: IObject;

  constructor(
    type: EElementType,
    length: number,
    width: number,
    position: Vector3 = new Vector3(0, 0, 0),
    rotation: number = 0,
    texture?: any | null,
    description?: string,
    useAsTextField?: boolean,
    fontSize?: number,
    object?: IObject,
    attributes: Array<IAttribute> = [],
    sideC?: number,
    triangleAngle?: number,
  ) {
    super(attributes);
    this.type = type;
    this.pathToSvg = ElementModel.getSvgPath(type);
    this.length = length;
    this.width = width;
    this.sideC = sideC;
    this.position = position;
    this.rotation = rotation;
    this.texture = texture ?? DEFAULT_EMPTY_TEXTURE;
    this.description = description;
    this.useAsTextField = useAsTextField;
    this.fontSize = fontSize;
    this.object = object;
    this.sideC = sideC;
    this.triangleAngle = triangleAngle;
  }

  static getSvgPath(type: EElementType): string {
    switch (type) {
      case EElementType.RECTANGLE:
        return rectangleSVG;
      case EElementType.CIRCLE:
        return circleSVG;
      case EElementType.TRIANGLE:
        // Return a dummy path (e.g. rectangle) to satisfy the loader,
        // but we will generate geometry procedurally.
        return rectangleSVG;
      default:
        throw new Error('Invalid element type');
    }
  }

  /** * Alias for `length`.
   * Represents the Vertical side (Left) of the triangle.
   */
  get sideA(): number {
    return this.length;
  }

  set sideA(value: number) {
    this.length = value;
  }

  /** * Alias for `width`.
   * Represents the Horizontal side (Bottom) of the triangle.
   */
  get sideB(): number {
    return this.width;
  }

  set sideB(value: number) {
    this.width = value;
  }

  getType(): EElementType {
    return this.type;
  }

  setType(type: EElementType): void {
    this.type = type;
  }

  getLength(): number {
    return this.length;
  }

  setLength(length: number): void {
    this.length = length;
  }

  getWidth(): number {
    return this.width;
  }

  setWidth(width: number): void {
    this.width = width;
  }


  getSideC(): number | undefined {
    return this.sideC;
  }

  setSideC(sideC: number): void {
    this.sideC = sideC;
  }

  getPosition(): Vector3 {
    return this.position;
  }

  setPosition(position: Vector3): void {
    this.position = position;
  }

  getRotation(): number | undefined {
    return this.rotation;
  }

  setRotation(rotation: number): void {
    this.rotation = rotation;
  }

  getDescription(): string | undefined {
    return this.description;
  }

  setDescription(description: string): void {
    this.description = description;
  }

  getTexture(): any | null | undefined {
    return this.texture;
  }

  setTexture(texture: any | null): void {
    this.texture = texture;
  }

  getUseAsTextField(): boolean | undefined {
    return this.useAsTextField;
  }

  setUseAsTextField(useAsTextField: boolean): void {
    this.useAsTextField = useAsTextField;
  }

  getFontSize(): number | undefined {
    return this.fontSize;
  }

  setFontSize(fontSize: number): void {
    this.fontSize = fontSize;
  }

  getTriangleAngle(): number | undefined {
    return this.triangleAngle;
  }

  setTriangleAngle(angle: number): void {
    this.triangleAngle = angle;
  }

  toJSON() {
    return {
      ...super.toJSON(),
      type: this.type,
      length: this.length,
      width: this.width,
      sideC: this.sideC,
      triangleAngle: this.triangleAngle,
      position: { x: this.position.x, y: this.position.y, z: this.position.z },
      rotation: this.rotation,
      texture: serializeTexture(this.texture),
      description: this.description,
      useAsTextField: this.useAsTextField,
      fontSize: this.fontSize,
      object: this.object,
    };
  }
}

export default ElementModel;